enum SessionTypeEnum {
  Desktop = 'desktop',
  Mobile = 'mobile'
}

enum SessionStatusEnum {
  New = 'new',
  SentForProcessing = 'sent_for_processing',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed'
}

const sessionStatusMap = {
  [SessionStatusEnum.New]: 'gray',
  [SessionStatusEnum.SentForProcessing]: 'info',
  [SessionStatusEnum.Processing]: 'info',
  [SessionStatusEnum.Completed]: 'warning',
  [SessionStatusEnum.Failed]: 'danger'
};

enum BillingCodesEnum {
  Tardoc = 'tardoc',
  Icd = 'icd',
  Tarmed = 'tarmed'
}

enum GendersEnum {
  Male = 'male',
  Female = 'female',
  Other = 'other'
}

enum FileTypeEnum {
  Document = 'document',
  Recording = 'recording',
  Photo = 'photo',
  Dictation = 'dictation',
  FormTemplate = 'form_template'
}

enum FileContentTypeEnum {
  Pdf = 'application/pdf',
  Doc = 'application/msword',
  Docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  Xls = 'application/vnd.ms-excel',
  Xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  Pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  Txt = 'text/plain',

  Png = 'image/png',
  Jpg = 'image/jpeg',

  Mp3 = 'audio/mpeg',
  Mp4 = 'audio/mp4',
  Wav = 'audio/wav',
  Aac = 'audio/aac',
  Webm = 'audio/webm'
}

enum FileExtensionEnum {
  Mp4 = '.mp4',
  Webm = '.webm'
}

enum RecordingStatusEnum {
  PendingTranscription = 'pending_transcription',
  InProgress = 'in_progress',
  TranscriptionCompleted = 'transcription_completed',
  Failed = 'failed'
}

enum DocumentStatusEnum {
  PendingTextRepresentation = 'pending_text_representation',
  Processing = 'processing',
  TextRepresentationCompleted = 'text_representation_completed',
  Failed = 'failed'
}

enum DocumentSupportedFileExtensionEnum {
  Pdf = 'pdf',
  Doc = 'doc',
  Docx = 'docx',
  Xls = 'xls',
  Xlsx = 'xlsx',
  Txt = 'txt',
  Pptx = 'pptx',

  Png = 'png',
  Jpg = 'jpg',
  Jpeg = 'jpeg',

  Mp3 = 'mp3',
  Mp4 = 'mp4',
  Wav = 'wav',
  Aac = 'aac'
}

const videoMp4 = 'video/mp4';

const documentSupportedFileExtensionsMap = {
  [FileTypeEnum.Document]: [
    DocumentSupportedFileExtensionEnum.Pdf,
    DocumentSupportedFileExtensionEnum.Doc,
    DocumentSupportedFileExtensionEnum.Docx,
    DocumentSupportedFileExtensionEnum.Xls,
    DocumentSupportedFileExtensionEnum.Xlsx,
    DocumentSupportedFileExtensionEnum.Txt,
    DocumentSupportedFileExtensionEnum.Pptx
  ],
  [FileTypeEnum.Photo]: [
    DocumentSupportedFileExtensionEnum.Png,
    DocumentSupportedFileExtensionEnum.Jpg,
    DocumentSupportedFileExtensionEnum.Jpeg
  ],
  [FileTypeEnum.Recording]: [
    DocumentSupportedFileExtensionEnum.Mp3,
    DocumentSupportedFileExtensionEnum.Mp4,
    DocumentSupportedFileExtensionEnum.Wav,
    DocumentSupportedFileExtensionEnum.Aac
  ]
};

const documentSupportedFileMimeTypesMap = {
  [FileContentTypeEnum.Pdf]: FileTypeEnum.Document,
  [FileContentTypeEnum.Doc]: FileTypeEnum.Document,
  [FileContentTypeEnum.Docx]: FileTypeEnum.Document,
  [FileContentTypeEnum.Xls]: FileTypeEnum.Document,
  [FileContentTypeEnum.Xlsx]: FileTypeEnum.Document,
  [FileContentTypeEnum.Txt]: FileTypeEnum.Document,
  [FileContentTypeEnum.Pptx]: FileTypeEnum.Document,

  [FileContentTypeEnum.Png]: FileTypeEnum.Photo,
  [FileContentTypeEnum.Jpg]: FileTypeEnum.Photo,

  [FileContentTypeEnum.Mp3]: FileTypeEnum.Recording,
  [FileContentTypeEnum.Mp4]: FileTypeEnum.Recording,
  [videoMp4]: FileTypeEnum.Recording,
  [FileContentTypeEnum.Wav]: FileTypeEnum.Recording,
  [FileContentTypeEnum.Aac]: FileTypeEnum.Recording
};

enum SummarizationReportStatusEnum {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed'
}

enum SummarizationReportMessageStatusEnum {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed'
}

enum SummarizationReportMessageTypeEnum {
  Summary = 'summary',
  User = 'user'
}

enum DictationTaskStatusEnum {
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed'
}

enum RecordingSourceEnum {
  App = 'app',
  External = 'external'
}

enum ExternalProviderEnum {
  // Universal query param to determine the provider
  company_slug = 'company_slug',
  // Providers
  axenita = 'axenita',
  wepractice = 'wepractice',
  smr = 'smr',
  cgm_phoenix = 'cgm_phoenix',

  // This query for WePractice to specify template for session creation (e.g. SOAP, HPI, etc.)
  template_id = 'template_id',
  // This query for SMR to specify template for session creation (e.g. SOAP, HPI, etc.)
  input_type = 'input_type',
  // When 'true' (together with a valid template_id), the report format is locked and cannot be changed by the user
  lock_template = 'lock_template'
}

const externalProviderNameMap = {
  [ExternalProviderEnum.axenita]: 'Axenita',
  [ExternalProviderEnum.wepractice]: 'WePractice',
  [ExternalProviderEnum.smr]: 'SMR'
  // [ExternalProviderEnum.cgm_phoenix]: 'CGM Phoenix'
};

enum AxenitaPatientDataJSONFieldKeysEnum {
  patientId = 'patientId',
  patientIdSystem = 'patientIdSystem',
  oid = 'oid',
  base_url = 'base_url',
  firstName = 'firstName',
  lastName = 'lastName',
  sex = 'sex',
  birthday = 'birthday'
}

const axenitaRequiredParams = [
  AxenitaPatientDataJSONFieldKeysEnum.patientId,
  AxenitaPatientDataJSONFieldKeysEnum.patientIdSystem,
  AxenitaPatientDataJSONFieldKeysEnum.oid
];

enum WePracticePatientDataJSONFieldKeysEnum {
  patient_id = 'patient_id',
  patient_name = 'patient_name',
  template_id = 'template_id',
  therapist_email = 'therapist_email',
  token = 'token'
}

const wePracticeRequiredParams = [
  WePracticePatientDataJSONFieldKeysEnum.patient_id,
  WePracticePatientDataJSONFieldKeysEnum.patient_name
];

enum SMRPatientDataJSONFieldKeysEnum {
  pid = 'pid',
  uid = 'uid',
  location = 'location',
  input_type = 'input_type',
  input_id = 'input_id',
  firstName = 'firstName',
  lastName = 'lastName'
}

const SMRRequiredParams = [
  SMRPatientDataJSONFieldKeysEnum.pid,
  SMRPatientDataJSONFieldKeysEnum.uid,
  SMRPatientDataJSONFieldKeysEnum.location,
  SMRPatientDataJSONFieldKeysEnum.input_type,
  SMRPatientDataJSONFieldKeysEnum.input_id
];

const SMRNumericParams = [
  SMRPatientDataJSONFieldKeysEnum.pid,
  SMRPatientDataJSONFieldKeysEnum.uid,
  SMRPatientDataJSONFieldKeysEnum.location,
  SMRPatientDataJSONFieldKeysEnum.input_type,
  SMRPatientDataJSONFieldKeysEnum.input_id
];

enum CGMPhoenixDataJSONFieldKeysEnum {
  firstName = 'firstName',
  lastName = 'lastName'
}

const CGMPhoenixRequiredParams: Array<string> = [];

export {
  SessionTypeEnum,
  SessionStatusEnum,
  sessionStatusMap,
  BillingCodesEnum,
  GendersEnum,
  FileTypeEnum,
  FileContentTypeEnum,
  FileExtensionEnum,
  RecordingStatusEnum,
  DocumentStatusEnum,
  DocumentSupportedFileExtensionEnum,
  documentSupportedFileExtensionsMap,
  documentSupportedFileMimeTypesMap,
  SummarizationReportStatusEnum,
  SummarizationReportMessageStatusEnum,
  SummarizationReportMessageTypeEnum,
  DictationTaskStatusEnum,
  RecordingSourceEnum,
  ExternalProviderEnum,
  externalProviderNameMap,
  AxenitaPatientDataJSONFieldKeysEnum,
  axenitaRequiredParams,
  WePracticePatientDataJSONFieldKeysEnum,
  wePracticeRequiredParams,
  SMRPatientDataJSONFieldKeysEnum,
  SMRRequiredParams,
  SMRNumericParams,
  CGMPhoenixDataJSONFieldKeysEnum,
  CGMPhoenixRequiredParams
};
